import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, GraduationCap, ShieldCheck, Users } from 'lucide-react';
import supabase from '../../supabaseClient';
import { isStatboxDesktop } from '../../lib/statboxDesktop';
import {
  USERS_LOGIN_SELECT,
  accessiblePortals,
  routePortalAfterAuth,
} from '../../utils/signupFunnelRedirect';

/**
 * Other workspaces on the same signed-in account. Shown in each portal's
 * account menu so a person who can open more than one (community, academy,
 * venue, staff tools) can move between them without signing out.
 */

const META = {
  community: { title: 'Community', Icon: Users },
  academy: { title: 'Academy', Icon: GraduationCap },
  venue: { title: 'Venue', Icon: Building2 },
  admin: { title: 'Staff tools', Icon: ShieldCheck },
};

const CACHE_MS = 20_000;
let profileCache = { userId: null, profile: null, communityName: null, loadedAt: 0 };

function looksLikeId(value) {
  const text = String(value || '').trim();
  if (!text || text === 'pending') return true;
  return /^[0-9a-f-]{16,}$/i.test(text);
}

function subtitleFor(portal, profile, communityName) {
  if (portal === 'community') return communityName || null;
  if (portal === 'academy') return profile?.academies?.name || null;
  if (portal === 'venue') {
    const name = String(profile?.venue_owner || '').trim();
    return looksLikeId(name) ? null : name;
  }
  return null;
}

async function loadAccount() {
  const { data: sessionData } = await supabase.auth.getSession();
  const userId = sessionData?.session?.user?.id || null;
  if (!userId) return { userId: null, profile: null, communityName: null };

  const fresh =
    profileCache.userId === userId && Date.now() - profileCache.loadedAt < CACHE_MS;
  if (fresh) return profileCache;

  const { data: profile, error } = await supabase
    .from('Users')
    .select(USERS_LOGIN_SELECT)
    .eq('user_id', userId)
    .maybeSingle();

  if (error || !profile) {
    return { userId, profile: null, communityName: null };
  }

  let communityName = null;
  if (profile.community_id && accessiblePortals(profile).includes('community')) {
    const { data } = await supabase
      .from('communities')
      .select('name')
      .eq('id', profile.community_id)
      .maybeSingle();
    communityName = data?.name || null;
  }

  profileCache = { userId, profile, communityName, loadedAt: Date.now() };
  return profileCache;
}

export function useWorkspaceSwitch(current) {
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [switching, setSwitching] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isStatboxDesktop()) {
      setAccount({ userId: null, profile: null, communityName: null });
      return undefined;
    }

    let cancelled = false;
    loadAccount()
      .then((loaded) => {
        if (!cancelled) setAccount(loaded);
      })
      .catch((err) => {
        console.error('Workspace switch: could not load account', err);
        if (!cancelled) setAccount({ userId: null, profile: null, communityName: null });
      });

    return () => {
      cancelled = true;
    };
  }, [current]);

  const targets = (account?.profile ? accessiblePortals(account.profile) : [])
    .filter((portal) => portal !== current && META[portal])
    .map((portal) => ({
      id: portal,
      title: META[portal].title,
      subtitle: subtitleFor(portal, account.profile, account.communityName),
      Icon: META[portal].Icon,
    }));

  const switchTo = async (portal) => {
    if (switching || !account?.profile || !account.userId) return;
    setSwitching(portal);
    setError('');
    try {
      const handled = await routePortalAfterAuth(navigate, account.profile, portal, {
        blockAutoRedirect: false,
        userId: account.userId,
      });
      if (!handled) {
        setError('That workspace is no longer on this account.');
        setSwitching(null);
      }
    } catch (err) {
      console.error('Workspace switch failed:', err);
      setError('Could not open that workspace. Try again.');
      setSwitching(null);
    }
  };

  return { targets, switching, error, switchTo };
}

const LIGHT_ROW = {
  community:
    'w-full flex items-center gap-3 px-4 py-2 text-left text-sm text-[#111111] hover:bg-[#F7F7F8] disabled:opacity-50 focus-visible:outline-none focus-visible:bg-[#F7F7F8]',
  venue:
    'w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-sm text-[#444444] hover:bg-[#F4F5F7] disabled:opacity-50 focus-visible:outline-none focus-visible:bg-[#F4F5F7]',
  'admin-header':
    'w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50 focus-visible:outline-none focus-visible:bg-slate-50',
};

const LIGHT_LABEL = {
  community: 'px-4 pt-2 pb-1 text-[11px] font-medium text-slate-500',
  venue: 'px-3 pt-2.5 pb-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[#5C6370]',
  'admin-header': 'px-4 pt-2.5 pb-1 text-[11px] font-semibold text-slate-500 uppercase tracking-wider',
};

const LIGHT_WRAP = {
  community: 'border-b border-slate-100 pb-1',
  venue: 'border-b border-[#E6E8EC]',
  'admin-header': 'border-b border-slate-100',
};

function LightList({ targets, switching, error, switchTo, tone }) {
  return (
    <div className={LIGHT_WRAP[tone]}>
      <p className={LIGHT_LABEL[tone]}>Also on this account</p>
      {targets.map((target) => {
        const busy = switching === target.id;
        const Icon = target.Icon;
        return (
          <button
            key={target.id}
            type="button"
            onClick={() => switchTo(target.id)}
            disabled={!!switching}
            aria-busy={busy || undefined}
            aria-label={busy ? `Opening ${target.title}` : `Open ${target.title}${target.subtitle ? `, ${target.subtitle}` : ''}`}
            className={LIGHT_ROW[tone]}
          >
            <Icon className="h-4 w-4 shrink-0 text-[#8A909A]" aria-hidden />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">{target.title}</span>
              {(busy || target.subtitle) && (
                <span className="block truncate text-xs text-slate-500">
                  {busy ? 'Opening…' : target.subtitle}
                </span>
              )}
            </span>
          </button>
        );
      })}
      {error ? (
        <p className="px-4 pb-2 text-xs text-rose-600" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function SidebarList({ targets, switching, error, switchTo, collapsed }) {
  return (
    <div className={collapsed ? 'space-y-1' : 'space-y-0.5 pb-1'}>
      {!collapsed && (
        <p className="px-3 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-600">
          Also on this account
        </p>
      )}
      {targets.map((target) => {
        const busy = switching === target.id;
        const Icon = target.Icon;
        return (
          <button
            key={target.id}
            type="button"
            title={collapsed ? target.title : undefined}
            onClick={() => switchTo(target.id)}
            disabled={!!switching}
            aria-label={collapsed ? `Open ${target.title}` : undefined}
            aria-busy={busy || undefined}
            className={`group w-full flex items-center rounded-lg text-zinc-300 hover:text-white hover:bg-white/5 disabled:opacity-50 focus-visible:outline-none focus-visible:bg-white/10 ${
              collapsed ? 'justify-center p-2.5' : 'gap-2.5 px-3 py-2'
            }`}
          >
            <Icon className="h-[18px] w-[18px] shrink-0 text-zinc-500 group-hover:text-zinc-300" aria-hidden />
            {!collapsed && (
              <span className="min-w-0 text-left">
                <span className="block truncate text-[13px] font-medium">
                  {busy ? 'Opening…' : target.title}
                </span>
                {!busy && target.subtitle ? (
                  <span className="block truncate text-[11px] text-zinc-500">{target.subtitle}</span>
                ) : null}
              </span>
            )}
          </button>
        );
      })}
      {error && !collapsed ? (
        <p className="px-3 pb-1 text-[11px] text-rose-300" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export default function WorkspaceSwitchMenu({
  tone = 'community',
  collapsed = false,
  targets,
  switching,
  error,
  switchTo,
}) {
  if (!targets?.length) return null;

  if (tone === 'admin-sidebar') {
    return (
      <SidebarList
        targets={targets}
        switching={switching}
        error={error}
        switchTo={switchTo}
        collapsed={collapsed}
      />
    );
  }

  return (
    <LightList
      targets={targets}
      switching={switching}
      error={error}
      switchTo={switchTo}
      tone={tone}
    />
  );
}
